import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import { Box, Dialog, DialogTitle, DialogContent, LinearProgress, Paper, Stack, Typography, IconButton, Tooltip } from "@mui/material";
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import CloseIcon from '@mui/icons-material/Close';
import MinimizeIcon from '@mui/icons-material/Minimize';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';

import { CreateImageAsset, UploadVideo, UploadThumbnail, updateAssetState } from '../../axios/ApiProvider';

const ASSET_CATEGORY_VIDEO = 1;
const ASSET_CATEGORY_IMAGE = 3;
const ASSET_STATE_UPLOAD_SUCCESS = 2;
const VIDEO_THUMB_COUNT = 6;
const THUMB_MAX_DIM = 480;

// On a failed byte-upload, wait this long (a visible countdown) and retry, up to
// this many times, before giving up.
const UPLOAD_RETRY_LIMIT = 3;
const UPLOAD_RETRY_DELAY_SEC = 10;
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const categoryFor = (mime) => {
  if (!mime) return null;
  if (mime.startsWith('video/')) return ASSET_CATEGORY_VIDEO;
  if (mime.startsWith('image/')) return ASSET_CATEGORY_IMAGE;
  return null;
};

const captureVideoFrame = (video, t) => new Promise((resolve, reject) => {
  const onSeeked = () => {
    video.removeEventListener('seeked', onSeeked);
    const w = video.videoWidth;
    const h = video.videoHeight;
    const scale = Math.min(1, THUMB_MAX_DIM / Math.max(w, h));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(w * scale));
    canvas.height = Math.max(1, Math.round(h * scale));
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (!blob) return reject(new Error('toBlob failed'));
      resolve(new File([blob], `thumb_${t.toFixed(2)}.jpg`, { type: 'image/jpeg' }));
    }, 'image/jpeg', 0.8);
  };
  video.addEventListener('seeked', onSeeked, { once: true });
  video.currentTime = Math.min(Math.max(t, 0), Math.max(0, video.duration - 0.05));
});

const generateVideoThumbnails = (file, count) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const video = document.createElement('video');
  video.muted = true;
  video.preload = 'metadata';
  video.crossOrigin = 'anonymous';
  video.src = url;

  const cleanup = () => { try { URL.revokeObjectURL(url); } catch (e) { /* noop */ } };

  video.addEventListener('error', () => { cleanup(); reject(new Error('video load error')); });
  video.addEventListener('loadedmetadata', async () => {
    try {
      const duration = isFinite(video.duration) && video.duration > 0 ? video.duration : 1;
      const out = [];
      for (let i = 0; i < count; i++) {
        const t = (duration / (count + 1)) * (i + 1);
        // eslint-disable-next-line no-await-in-loop
        const f = await captureVideoFrame(video, t);
        out.push({ file: f, time: t });
      }
      cleanup();
      resolve(out);
    } catch (e) {
      cleanup();
      reject(e);
    }
  });
});

const generateImageThumbnail = (file) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(file);
  const img = new Image();
  img.onload = () => {
    const scale = Math.min(1, THUMB_MAX_DIM / Math.max(img.width, img.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(img.width * scale));
    canvas.height = Math.max(1, Math.round(img.height * scale));
    canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      URL.revokeObjectURL(url);
      if (!blob) return reject(new Error('toBlob failed'));
      resolve([{ file: new File([blob], 'thumb.jpg', { type: 'image/jpeg' }), time: 0 }]);
    }, 'image/jpeg', 0.85);
  };
  img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('image load error')); };
  img.src = url;
});

const BulkUploadContext = createContext(null);

export const useBulkUpload = () => {
  const ctx = useContext(BulkUploadContext);
  if (!ctx) throw new Error('useBulkUpload must be used inside BulkUploadProvider');
  return ctx;
};

export const BulkUploadProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [items, setItems] = useState([]);
  const [running, setRunning] = useState(false);
  const onCompleteRef = useRef(null);
  const onItemDoneRef = useRef(null);

  const updateItem = useCallback((idx, patch) => {
    setItems((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  }, []);

  const uploadOne = useCallback(async (file, idx) => {
    const category = categoryFor(file.type);
    if (!category) {
      updateItem(idx, { state: 'error', message: 'Unsupported file type' });
      return null;
    }

    updateItem(idx, { state: 'thumbs' });
    let thumbs = [];
    try {
      thumbs = category === ASSET_CATEGORY_VIDEO
        ? await generateVideoThumbnails(file, VIDEO_THUMB_COUNT)
        : await generateImageThumbnail(file);
    } catch (e) {
      thumbs = [];
    }

    const thumbnail_info = JSON.stringify(thumbs.map((t) => ({
      timestamp: t.time,
      content_type: t.file.type || 'image/jpeg',
      url: null,
    })));

    updateItem(idx, { state: 'creating' });
    const created = await CreateImageAsset({
      title: file.name,
      original_filename: file.name,
      mime_type: file.type,
      category,
      size: file.size,
      thumbnail_info,
    });
    if (created.state !== 'success' || !created.data?.signedurl) {
      updateItem(idx, { state: 'error', message: created.data?.message || 'create failed' });
      return null;
    }
    const assetUUID = created.data.uuid;
    const signedurl = created.data.signedurl;
    const thumbInfoJson = created.data.thumbnail_info_json || {};

    updateItem(idx, { state: 'uploading', progress: 0, uuid: assetUUID });
    let uploaded = await UploadVideo(file, signedurl, (pc) => updateItem(idx, { progress: pc }));

    // On failure, count down 10s and retry the byte-upload. We re-PUT to the SAME
    // signed URL, so no duplicate asset record is created. Up to 3 retries.
    for (let attempt = 1; uploaded.state !== 'success' && attempt <= UPLOAD_RETRY_LIMIT; attempt += 1) {
      for (let s = UPLOAD_RETRY_DELAY_SEC; s > 0; s -= 1) {
        updateItem(idx, { state: 'retrying', progress: 0, message: `Upload failed — retry ${attempt}/${UPLOAD_RETRY_LIMIT} in ${s}s…` });
        // eslint-disable-next-line no-await-in-loop
        await sleep(1000);
      }
      updateItem(idx, { state: 'uploading', progress: 0, message: undefined });
      // eslint-disable-next-line no-await-in-loop
      uploaded = await UploadVideo(file, signedurl, (pc) => updateItem(idx, { progress: pc }));
    }

    if (uploaded.state !== 'success') {
      updateItem(idx, { state: 'error', message: uploaded.data?.message || `upload failed after ${UPLOAD_RETRY_LIMIT} retries` });
      return null;
    }

    const thumbEntries = Object.values(thumbInfoJson);
    for (let i = 0; i < thumbs.length && i < thumbEntries.length; i++) {
      const su = thumbEntries[i]?.signedurl;
      if (!su) continue;
      // eslint-disable-next-line no-await-in-loop
      await UploadThumbnail(su, thumbs[i].file);
    }

    await updateAssetState(assetUUID, ASSET_STATE_UPLOAD_SUCCESS, 100);
    updateItem(idx, { state: 'done', progress: 100 });
    return { uuid: assetUUID, name: file.name, category, type: file.type };
  }, [updateItem]);

  const startUpload = useCallback((files, onComplete, opts = {}) => {
    if (!files || files.length === 0) return;
    onCompleteRef.current = typeof onComplete === 'function' ? onComplete : null;
    // onItemDone(index, result) fires as each file settles (result is the
    // asset info on success, or null on failure) so callers can react to
    // uploads incrementally instead of waiting for the whole batch.
    onItemDoneRef.current = typeof opts.onItemDone === 'function' ? opts.onItemDone : null;
    const initial = files.map((f) => ({
      name: f.name,
      size: f.size,
      type: f.type,
      progress: 0,
      state: 'queued',
    }));
    setItems(initial);
    // Callers (e.g. the Planner Wizard) can start minimized so the upload shows
    // as a non-blocking floating card instead of the modal dialog, letting the
    // user keep working while files upload.
    setMinimized(!!opts.minimized);
    setOpen(!opts.minimized);
    (async () => {
      setRunning(true);
      const succeeded = [];
      for (let i = 0; i < files.length; i++) {
        // eslint-disable-next-line no-await-in-loop
        const res = await uploadOne(files[i], i);
        if (res) succeeded.push(res);
        if (onItemDoneRef.current) {
          try { onItemDoneRef.current(i, res); } catch (e) { /* noop */ }
        }
      }
      setRunning(false);
      if (succeeded.length && onCompleteRef.current) {
        // onComplete receives the list of successfully uploaded assets
        // ([{ uuid, name, category, type }]); existing callers ignore it.
        try { onCompleteRef.current(succeeded); } catch (e) { /* noop */ }
      }
    })();
  }, [uploadOne]);

  const handleMinimize = () => {
    setOpen(false);
    setMinimized(true);
  };

  const handleExpand = () => {
    setMinimized(false);
    setOpen(true);
  };

  const handleClose = () => {
    if (running) {
      handleMinimize();
      return;
    }
    setOpen(false);
    setMinimized(false);
    setItems([]);
  };

  const handleDismissFloating = () => {
    if (running) return;
    setMinimized(false);
    setItems([]);
  };

  const total = items.length;
  const doneCount = items.filter((it) => it.state === 'done').length;
  const errorCount = items.filter((it) => it.state === 'error').length;
  const overallProgress = total === 0
    ? 0
    : Math.round(items.reduce((sum, it) => {
        if (it.state === 'done') return sum + 100;
        if (it.state === 'error') return sum + 100;
        if (it.state === 'uploading') return sum + (it.progress || 0);
        return sum;
      }, 0) / total);

  const value = useMemo(() => ({ startUpload, running, hasItems: items.length > 0 }), [startUpload, running, items.length]);

  return (
    <BulkUploadContext.Provider value={value}>
      {children}

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>Bulk Upload</span>
          <Box>
            <Tooltip title="Minimize">
              <IconButton onClick={handleMinimize} size="small" sx={{ mr: 0.5 }}>
                <MinimizeIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={running ? 'Minimize (uploads still running)' : 'Close'}>
              <span>
                <IconButton onClick={handleClose} size="small">
                  <CloseIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            {items.map((it, idx) => (
              <Box key={idx}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  {it.state === 'done' && <CheckCircleIcon color="success" fontSize="small" />}
                  {it.state === 'error' && <ErrorIcon color="error" fontSize="small" />}
                  <Typography variant="body2" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {it.name}
                  </Typography>
                  <Typography variant="caption" sx={{ color: it.state === 'retrying' ? 'warning.main' : 'text.secondary' }}>
                    {it.state === 'queued' && 'Queued'}
                    {it.state === 'thumbs' && 'Thumbnails…'}
                    {it.state === 'creating' && 'Preparing…'}
                    {it.state === 'uploading' && `${it.progress}%`}
                    {it.state === 'retrying' && (it.message || 'Retrying…')}
                    {it.state === 'done' && 'Done'}
                    {it.state === 'error' && (it.message || 'Failed')}
                  </Typography>
                </Stack>
                {(it.state === 'uploading' || it.state === 'creating' || it.state === 'thumbs' || it.state === 'retrying') && (
                  <LinearProgress
                    variant={it.state === 'uploading' ? 'determinate' : 'indeterminate'}
                    value={it.progress || 0}
                    color={it.state === 'retrying' ? 'warning' : 'primary'}
                    sx={{ mt: 0.5 }}
                  />
                )}
              </Box>
            ))}
          </Stack>
        </DialogContent>
      </Dialog>

      {minimized && items.length > 0 && (
        <Paper
          elevation={6}
          sx={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            width: 320,
            maxWidth: 'calc(100vw - 32px)',
            p: 1.5,
            borderRadius: 2,
            zIndex: (theme) => theme.zIndex.modal + 1,
            cursor: 'pointer',
          }}
          onClick={handleExpand}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <CloudUploadIcon fontSize="small" color="primary" />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                {running ? 'Uploading…' : 'Uploads complete'}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {doneCount}/{total} done
                {errorCount > 0 ? ` · ${errorCount} failed` : ''}
                {running ? ` · ${overallProgress}%` : ''}
              </Typography>
            </Box>
            <Tooltip title="Expand">
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleExpand(); }}>
                <OpenInFullIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={running ? 'Cannot dismiss while uploading' : 'Dismiss'}>
              <span>
                <IconButton
                  size="small"
                  disabled={running}
                  onClick={(e) => { e.stopPropagation(); handleDismissFloating(); }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={overallProgress}
            sx={{ mt: 1, height: 4, borderRadius: 2 }}
          />
        </Paper>
      )}
    </BulkUploadContext.Provider>
  );
};

export default BulkUploadProvider;
